// filepath: apps/web/src/features/admin/effects/HttpEndpointFormDialog.tsx
//
// FR-UI-ADM-8 -- create form dialog for HTTP endpoints.
//
// INV-5 contract (central): on create success, the response
// carries `auth_secret_value` EXACTLY ONCE. This component renders
// it on a one-time success screen and clears it (via
// successAuthSecretValue) when the dialog closes.
//
// The parent (HttpEndpointsPage) owns the `successAuthSecretValue`
// state and passes it as a controlled prop (post-ISS-206 pattern
// from FR-UI-ADM-6).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { CreateHttpEndpointRequest } from "./types";

interface HttpEndpointFormDialogProps {
  open: boolean;
  onSubmit: (req: CreateHttpEndpointRequest) => Promise<void>;
  onClose: () => void;
  isPending: boolean;
  successAuthSecretValue?: string | null;
  error?: string | null;
}

export function HttpEndpointFormDialog({
  open,
  onSubmit,
  onClose,
  isPending,
  successAuthSecretValue,
  error,
}: HttpEndpointFormDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState("POST");
  const [timeout, setTimeout] = useState(30);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  // One-time success screen (INV-5).
  if (successAuthSecretValue !== null && successAuthSecretValue !== undefined) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="http-endpoint-create-success-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        data-testid="http-endpoint-create-success-dialog"
      >
        <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
          <h2
            id="http-endpoint-create-success-title"
            className="text-sm font-semibold text-green-600"
          >
            {t("admin.effects.http.dialog.create.success.title")}
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("admin.effects.http.dialog.create.success.description")}
          </p>
          <div className="mt-3 flex gap-2 items-center">
            <code
              className="flex-1 rounded bg-muted px-2 py-1 text-xs font-mono break-all"
              data-testid="http-endpoint-auth-secret-value"
            >
              {successAuthSecretValue}
            </code>
            <button
              type="button"
              className="shrink-0 rounded border border-border px-2 py-1 text-xs"
              onClick={() => {
                void navigator.clipboard.writeText(successAuthSecretValue);
                setCopied(true);
              }}
              data-testid="http-endpoint-copy-secret"
            >
              {copied
                ? t("common.copied")
                : t("admin.effects.http.dialog.create.success.copy_secret")}
            </button>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="rounded bg-primary px-4 py-2 text-xs text-primary-foreground"
              onClick={() => {
                setCopied(false);
                onClose();
              }}
              data-testid="http-endpoint-success-done"
            >
              {t("common.done")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const req: CreateHttpEndpointRequest = {
      url: url.trim(),
      method,
      timeout_seconds: timeout,
    };
    await onSubmit(req);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="http-endpoint-form-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="http-endpoint-form-dialog"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="http-endpoint-form-title" className="text-sm font-semibold">
          {t("admin.effects.http.dialog.create.title")}
        </h2>
        {error && (
          <p
            role="alert"
            className="mt-2 text-xs text-destructive"
            data-testid="http-endpoint-form-error"
          >
            {error}
          </p>
        )}
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-medium" htmlFor="http-ep-url">
              {t("admin.effects.http.field.url")}
            </label>
            <input
              id="http-ep-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mt-1 w-full rounded border border-input px-2 py-1 text-xs"
              required
              aria-required="true"
              data-testid="http-endpoint-url-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium" htmlFor="http-ep-method">
              {t("admin.effects.http.field.method")}
            </label>
            <select
              id="http-ep-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="mt-1 w-full rounded border border-input px-2 py-1 text-xs"
              data-testid="http-endpoint-method-select"
            >
              {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium" htmlFor="http-ep-timeout">
              {t("admin.effects.http.field.timeout")}
            </label>
            <input
              id="http-ep-timeout"
              type="number"
              min={1}
              max={300}
              value={timeout}
              onChange={(e) => setTimeout(Number(e.target.value))}
              className="mt-1 w-full rounded border border-input px-2 py-1 text-xs"
              data-testid="http-endpoint-timeout-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded border border-border px-3 py-1 text-xs"
              onClick={onClose}
              data-testid="http-endpoint-form-cancel"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground"
              disabled={isPending}
              data-testid="http-endpoint-form-submit"
            >
              {isPending ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
